import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    toggleDetectionsOpen,
    toggleSuspectViewerOpen,
    fetchInspectionSperryFaults,
    setTargetInpectionTimestamp,
    requestPlaylistTimestamp,
    updateSperryFilters,
} from "../../redux/actions";
import { addDetectionsTypeFilter, setActiveDetectionsTab } from "../../redux/actions/index";
import { calculatePulseFromTimestamp } from "./Geometry";
import _ from "lodash";

const REPLACE_EXISTING_FILTERS = true;
const detectionTypeFiltersSelector = (state) => state.railInspection.detections.typeFilters;
const classificationsDataSelector = (state) => state.railInspection.classifications.data;
const faultsSelector = (state) => state.railInspection.sperryFaults.faults;
const sessionIDSelector = (state) => state.playlist.data.routeID;
const inspectionPulseCountsSelector = (state) => state.railInspection.railInspectionPulseData;

const validateOptionsObject = (options) => {
    if (typeof options !== "object") {
        return false;
    }

    // Make sure options object has an options string key, and an array of values
    for (let key in options) {
        if (typeof key !== "string" || !Array.isArray(options[key])) {
            return false;
        }
    }

    return true;
};

// Make sure that the filters are cased correctly, regardless of what's passed in the URL. Filter types are always title case.
const ensureTitleCase = (arrayOfStrings) => {
    arrayOfStrings.forEach((string, index) => {
        const splitString = string.split(" ");
        if (splitString.length === 1) {
            arrayOfStrings[index] = string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
        } else {
            arrayOfStrings[index] = splitString
                .map((word) => {
                    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                })
                .join(" ");
        }
    });
};

const handleSetFilters = (options, dispatch) => {
    // Check if options was passed in the URL, then validate the shape
    const optionsObject = options ? JSON.parse(decodeURIComponent(options)) : {};

    if (!validateOptionsObject(optionsObject)) {
        return;
    }

    const detectionTypeFilters = optionsObject["detection_type"] || [];
    // If there are detection types, apply filters that were passed in
    if (detectionTypeFilters.length) {
        ensureTitleCase(detectionTypeFilters);
        dispatch(addDetectionsTypeFilter(detectionTypeFilters, REPLACE_EXISTING_FILTERS));
    }
};

const useOpenDialogFromURLParams = () => {
    const dispatch = useDispatch();
    const [isReady, setIsReady] = useState(false);
    const filterTypes = useSelector(detectionTypeFiltersSelector);
    const classificationsData = useSelector(classificationsDataSelector);
    const faults = useSelector(faultsSelector);
    const sessionID = useSelector(sessionIDSelector);
    const inspectionPulseCounts = useSelector(inspectionPulseCountsSelector);

    // Maps the values of the comma separated 'select' query param to the function that will handle each dialog. Can be extended for other things as well.
    const paramToDispatchMap = {
        detections: (options) => {
            // Detections are the default active tab, so no need to set it here
            dispatch(toggleDetectionsOpen());
            handleSetFilters(options, dispatch);
        },
        classifications: (options) => {
            dispatch(toggleDetectionsOpen());
            dispatch(setActiveDetectionsTab("Classifications"));
            handleSetFilters(options, dispatch);
        },
        utu_suspects: (options) => {
            const optionsObject = JSON.parse(options);
            dispatch(toggleSuspectViewerOpen());
            if (Object.keys(optionsObject).includes("suspect_id")) {
                const fault = _.find(faults, { uid: parseInt(optionsObject.suspect_id) });

                const { timestamp, interpolationRatio } = calculatePulseFromTimestamp(fault.pulse_count, inspectionPulseCounts);

                if (!_.isNil(interpolationRatio)) {
                    dispatch(setTargetInpectionTimestamp(timestamp, interpolationRatio));
                }

                if (timestamp) {
                    dispatch(requestPlaylistTimestamp(timestamp / 1000));
                }
                dispatch(updateSperryFilters(optionsObject.suspect_id));
            }
        },
    };

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    // Need this useEffect to trigger the one below when Redux has populated the filter store or classifications data if classifications passed as selected
    useEffect(() => {
        let select = queryParams.get("selected");
        if (select) {
            const dialogs = select.split(",");
            const lowerCaseDialogs = dialogs.map((str) => str.toLowerCase());

            // if selected = detections check if filters populated
            if (!isReady && _.includes(lowerCaseDialogs, "detections") && filterTypes.length) {
                setIsReady(true);
            }

            // if selected = classifications in url check if classifications data in redux
            if (!isReady && _.includes(lowerCaseDialogs, "classifications") && classificationsData.length) {
                setIsReady(true);
            }

            // if selected = utu_suspects in url
            if (!isReady && _.includes(lowerCaseDialogs, "utu_suspects") && Object.keys(inspectionPulseCounts).length > 0) {
                dispatch(fetchInspectionSperryFaults(sessionID)).then(() => {
                    setIsReady(true);
                });
            }
        }
    }, [filterTypes, classificationsData, isReady, sessionID, inspectionPulseCounts]);

    useEffect(() => {
        const select = queryParams.get("selected");
        if (select && isReady) {
            const dialogs = select.split(",");
            if (dialogs.length) {
                dialogs.forEach((dialog) => {
                    const handler = paramToDispatchMap[dialog.toLowerCase()];
                    const options = queryParams.get("options");
                    if (handler) {
                        handler(options);
                    }
                });
            }
        }
    }, [isReady]);
};

export default useOpenDialogFromURLParams;
